import ThemeModal from "components/Common/ThemeModal"
import { Badge, Button, Col, Row, Spinner } from "reactstrap"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { formatReadableDate } from "utils/formatDate"
import { formatCurrency } from "utils/formatCurrency"

const STATUS_COLORS = {
  pending: "warning",
  completed: "success",
  cancelled: "secondary",
}

const ReceiptRow = ({ label, value, mono = false, strong = false }) => {
  if (value !== null && value !== undefined && typeof value === "object") {
    console.warn(`ReceiptRow "${label}" received object value:`, value)
  }
  const displayValue =
    value === null || value === undefined
      ? "-"
      : typeof value === "object"
      ? value.name ?? value.label ?? value.title ?? "-"
      : value
  return (
    <div
      className="d-flex align-items-start justify-content-between gap-3 py-2"
      style={{ borderBottom: "1px dashed rgba(15, 23, 42, 0.12)" }}
    >
      <span
        className="text-muted text-uppercase fw-semibold"
        style={{ fontSize: 10, letterSpacing: 1 }}
      >
        {label}
      </span>
      <span
        className={`${mono ? "font-monospace" : ""} ${strong ? "fw-bold" : "fw-semibold"} text-dark text-end`}
        style={{ fontSize: strong ? 15 : 13 }}
      >
        {displayValue || "-"}
      </span>
    </div>
  )
}

const ReceiptSection = ({ title, children }) => (
  <div className="pt-3">
    <div
      className="text-uppercase text-muted fw-semibold mb-2"
      style={{ fontSize: 11, letterSpacing: 1 }}
    >
      {title}
    </div>
    {children}
  </div>
)

const AccountRtgsPaymentView = ({
  modal,
  toggleModal,
  rtgsPayment,
  loading,
  onComplete,
  onPdf,
  pdfLoadingId,
}) => {
  const r = rtgsPayment

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title="RTGS Payment Details"
      size="xl"
      modalBodyClass="bg-body-tertiary"
    >
      {loading ? (
        <div className="text-center py-4">
          <Spinner size="sm" />
        </div>
      ) : !r ? (
        <div className="text-center py-4 text-muted">No details available.</div>
      ) : (
        <div className="mx-auto" style={{ maxWidth: 780 }}>
          <div
            className="rounded-4 border overflow-hidden"
            style={{
              background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
              borderColor: "rgba(15, 23, 42, 0.08)",
              boxShadow: "0 24px 64px rgba(15, 23, 42, 0.08)",
            }}
          >
            {/* Header bar */}
            <div
              className="px-4 py-2 d-flex align-items-center justify-content-between"
              style={{
                background:
                  "linear-gradient(90deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 41, 59, 0.96) 100%)",
              }}
            >
              <div
                className="text-white text-uppercase fw-semibold"
                style={{ fontSize: 10, letterSpacing: 1.6 }}
              >
                RTGS Transfer
              </div>
              <div className="d-flex align-items-center gap-2">
                <Badge color={STATUS_COLORS[r.status] || "secondary"} pill>
                  {r.status || "pending"}
                </Badge>
                <span
                  className="text-white-50 font-monospace"
                  style={{ fontSize: 12 }}
                >
                  {r.rtgs_no}
                </span>
              </div>
            </div>

            {/* Amount hero */}
            <div
              className="px-4 py-3 border-bottom"
              style={{
                background:
                  "radial-gradient(circle at top left, rgba(59, 130, 246, 0.08), transparent 34%), linear-gradient(180deg, #f8fbfd 0%, #ffffff 100%)",
                borderColor: "rgba(15, 23, 42, 0.08)",
              }}
            >
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
                <div>
                  <div
                    className="text-uppercase text-muted fw-semibold mb-1"
                    style={{ fontSize: 10, letterSpacing: 1.2 }}
                  >
                    Beneficiary
                  </div>
                  <div className="h5 mb-1 text-dark">{r.beneficiary_name}</div>
                  <div className="text-muted small">
                    {r.beneficiary_bank_name} — {r.beneficiary_branch_name}
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    <span className="text-muted small font-monospace">
                      Acc: {r.beneficiary_account_number}
                    </span>
                    {r.beneficiary_routing_number && (
                      <span className="text-muted small font-monospace">
                        Routing: {r.beneficiary_routing_number}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-md-end">
                  <div
                    className="text-uppercase text-muted fw-semibold mb-1"
                    style={{ fontSize: 10, letterSpacing: 1.2 }}
                  >
                    Transfer Amount
                  </div>
                  <div
                    className="fw-bold text-dark"
                    style={{ fontSize: 34, lineHeight: 0.95, letterSpacing: -1 }}
                  >
                    ৳ {(r.amount || 0).toLocaleString()}
                  </div>
                  {r.amount_in_words && (
                    <div className="text-muted small mt-1">{r.amount_in_words}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Ticket divider */}
            <div className="position-relative" style={{ height: 18, background: "#fff" }}>
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: "50%",
                  borderTop: "1px dashed rgba(15, 23, 42, 0.18)",
                }}
              />
              <div
                className="position-absolute rounded-circle bg-body-tertiary"
                style={{ width: 22, height: 22, left: -11, top: -2 }}
              />
              <div
                className="position-absolute rounded-circle bg-body-tertiary"
                style={{ width: 22, height: 22, right: -11, top: -2 }}
              />
            </div>

            {/* Details */}
            <div
              className="px-4 py-3 border-bottom bg-white"
              style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}
            >
              <Row className="g-3 g-md-4">
                <Col md={6}>
                  <ReceiptSection title="Remitter (Part 1)">
                    <ReceiptRow label="Branch" value={r.branch_name} />
                    <ReceiptRow label="Date" value={r.date ? formatReadableDate(r.date) : "-"} />
                    <ReceiptRow label="Account Name" value={r.remitter_name} />
                    <ReceiptRow label="Account Number" value={r.remitter_account_number} mono />
                    <ReceiptRow label="Mobile" value={r.remitter_mobile} />
                    <ReceiptRow label="Purpose" value={r.purpose} />
                  </ReceiptSection>
                </Col>
                <Col md={6}>
                  <ReceiptSection title="Beneficiary (Part 2)">
                    <ReceiptRow label="Type" value={r.beneficiary_type || "-"} />
                    <ReceiptRow label="Name" value={r.beneficiary_name} />
                    <ReceiptRow label="Bank" value={r.beneficiary_bank_name} />
                    <ReceiptRow label="Branch" value={r.beneficiary_branch_name} />
                    <ReceiptRow label="Account Name" value={r.beneficiary_account_name} />
                    <ReceiptRow label="Account Number" value={r.beneficiary_account_number} mono />
                    <ReceiptRow label="Routing Number" value={r.beneficiary_routing_number} mono />
                    {r.beneficiary_type === "importer" && (r.beneficiaryImporter || r.beneficiaryAccount) ? (
                      <>
                        <ReceiptRow
                          label="Linked Importer"
                          value={r.beneficiaryImporter?.name || "-"}
                        />
                        <ReceiptRow
                          label="Linked Importer Account"
                          value={
                            r.beneficiaryAccount
                              ? `${r.beneficiaryAccount.bank_name || ""}${
                                  r.beneficiaryAccount.label
                                    ? ` (${r.beneficiaryAccount.label})`
                                    : ""
                                } — ${r.beneficiaryAccount.account_number || ""}`
                              : "-"
                          }
                          mono
                        />
                      </>
                    ) : null}
                  </ReceiptSection>
                </Col>
              </Row>
            </div>

            {/* Ledger info */}
            <div
              className="px-4 py-3 border-bottom bg-white"
              style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}
            >
              <ReceiptSection title="Ledger & Status">
                <div
                  className="rounded-3 px-3 py-2"
                  style={{
                    background: "rgba(248, 250, 252, 0.9)",
                    border: "1px solid rgba(15, 23, 42, 0.06)",
                  }}
                >
                  <ReceiptRow label="RTGS Reference No" value={r.rtgs_no} mono strong />
                  <ReceiptRow label="Status" value={r.status} />
                  <ReceiptRow
                    label="Debit Account"
                    value={r.account?.name || (r.account_id ? `Account #${r.account_id}` : "-")}
                  />
                  <ReceiptRow
                    label="Total Transfer"
                    value={`৳ ${(r.amount || 0).toLocaleString()}`}
                    strong
                    mono
                  />
                </div>
              </ReceiptSection>
            </div>

            {r.notes && (
              <div
                className="px-4 py-3 border-bottom bg-white"
                style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}
              >
                <ReceiptSection title="Notes">
                  <div
                    className="text-dark rounded-3 px-3 py-2"
                    style={{
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.5,
                      background:
                        "linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(255, 255, 255, 1) 100%)",
                      border: "1px solid rgba(15, 23, 42, 0.06)",
                    }}
                  >
                    {r.notes}
                  </div>
                </ReceiptSection>
              </div>
            )}

            {/* Action buttons */}
            <div
              className="px-4 py-3"
              style={{
                background:
                  "linear-gradient(180deg, rgba(248, 250, 252, 0.75) 0%, rgba(241, 245, 249, 0.9) 100%)",
              }}
            >
              <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <div>
                  <div
                    className="text-uppercase text-muted fw-semibold mb-1"
                    style={{ fontSize: 10, letterSpacing: 1.2 }}
                  >
                    Created
                  </div>
                  <div className="small text-dark">
                    {typeof r.createdBy === "object" && r.createdBy !== null
                      ? r.createdBy.name ?? r.createdBy.username ?? "-"
                      : r.createdBy || (typeof r.created_by === "object" && r.created_by !== null
                          ? r.created_by.name ?? r.created_by.username ?? "-"
                          : r.created_by) || "-"}
                  </div>
                  <div className="small text-muted">
                    {r.created_at ? formatReadableDate(r.created_at) : "-"}
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <Button
                    color="info"
                    outline
                    size="sm"
                    onClick={() => onPdf?.(r.id)}
                    disabled={pdfLoadingId === r.id}
                  >
                    <i
                      className={`mdi mdi-file-pdf-box me-1 ${
                        pdfLoadingId === r.id ? "mdi-spin" : ""
                      }`}
                    />
                    Download Form
                  </Button>

                  <CanView access={[ACCESS.ACCOUNT_RTGS_PAYMENT.COMPLETE]}>
                    {r.status === "pending" && (
                      <Button
                        color="success"
                        size="sm"
                        onClick={() => onComplete?.(r.id)}
                      >
                        <i className="mdi mdi-check-circle-outline me-1" />
                        Mark Complete
                      </Button>
                    )}
                  </CanView>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </ThemeModal>
  )
}

export default AccountRtgsPaymentView
